import React, { Suspense, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';

import { EcoSelectStation } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { getDynamicComponent } from '@/constants/componentsMap';
import { ComponentTierName } from '@/constants/enums';
import { ValueDashboardContainer } from '@/comp/pages/ValueDashboardPage/styled';

import EnergyEfficiencyYearlyView from './EnergyEfficiency/EnergyEfficiencyYearlyView';
import StationRiskYearlyView from './StationRisk/StationRiskYearlyView/StationRiskYearlyView';
import Insights from './Insights/Insights';
import StationStages from '@/comp/StationStages/StationStages';

const ValueDashboardPage = observer(({ tabKey }: { tabKey: string }) => {
  const navigate = useNavigate();
  const { stationRiskGraphStore, energySavingsGraphStore, stationStore, permissionStore } = useStores();

  const hasViewPermission = permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD);
  const currentTier = stationStore?.stationTier;
  const currentStationType = stationStore.currentStation.stationPressureType;
  const isWasteWaterTreatment = stationStore.isWasteWaterStation;

  //dynamic components
  const Leakage = getDynamicComponent(ComponentTierName.Leakage, currentTier);
  const StationRisk = getDynamicComponent(ComponentTierName.SystemHealth, currentTier);
  const EnergySavings = getDynamicComponent(ComponentTierName.EnergySavingsWidget, currentTier, currentStationType);

  useEffect(() => {
    if (!hasViewPermission) {
      navigate('/home');
      return;
    }
  }, [hasViewPermission]);

  return (
    <ValueDashboardContainer>
      <div className="top-row">
        <EcoSelectStation requiredPermission={permissions.VIEW_STATION_VALUE_DASHBOARD} />
        <StationStages />
      </div>
      <div className="grid-container">
        <Suspense>
          <EnergySavings className="card-item energy-savings" />

          {!isWasteWaterTreatment && (
            <EnergyEfficiencyYearlyView
              className="yearly-view"
              style={{ display: energySavingsGraphStore.yearlyViewOpen ? 'flex' : 'none' }}
              tabKey={tabKey}
            />
          )}

          {permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_INSIGHTS) && (
            <Insights className="card-item insight" />
          )}

          {permissionStore.isAuth(permissions.VIEW_STATION_VALUE_DASHBOARD_LEAKAGE) && !isWasteWaterTreatment && (
            <Leakage className="card-item leakage" />
          )}

          {!isWasteWaterTreatment && (
            <>
              <StationRisk className="card-item station-risk" />
              <StationRiskYearlyView
                className="yearly-view station-risk-yearly-view"
                style={{ display: stationRiskGraphStore.yearlyViewOpen ? 'flex' : 'none' }}
              />
            </>
          )}
        </Suspense>
      </div>
    </ValueDashboardContainer>
  );
});

export default ValueDashboardPage;
